/**
 * DR_META — capture au dessin.
 *
 * Estampe l'identité de création sur chaque entité fraîchement dessinée par DR :
 *   - `entityUid`  : identité stable, merge-safe (UUID, pas un compteur séquentiel)
 *   - `createdAt`  : epoch ms → ordre chronologique réel du fil « Activité »
 *   - `kind`       : nature CAD réelle (EntityType — text/polyline/mleader/...)
 *
 * Appelé depuis `BaseAddEntityCommand.execute()` (le point de passage unique des
 * ajouts d'entités). Idempotent : si l'entité porte déjà un DR_META, on n'y touche
 * pas → `entityUid`/`createdAt` sont préservés à travers redo / ré-exécution.
 *
 * Best-effort : ne doit jamais casser le dessin. Le n° de photo n'est PAS dupliqué
 * ici — il est déjà porté par le XData DR_PHOTO_MARKER et lu à l'affichage.
 */

import type { DRDrawingViewer } from "../DRDrawingViewer";
import { findEntityByHandle } from "../core/oda";
import { deleteObj } from "../core/delete";
import { writeEntityMeta, readEntityMeta } from "./xdata";
import { getEntityType, EntityType } from "../entities/detection";
import { v4 as uuid } from "uuid";

/** Estampe entityUid + createdAt + role sur les entités qui n'ont pas encore de DR_META. */
export function stampCreationMeta(
  viewer: DRDrawingViewer,
  handles: string[],
): void {
  const module = viewer.module;
  const db = viewer.database;
  if (!db || handles.length === 0) return;

  const now = Date.now();
  // Désordre actif au dessin (les draggers stylent l'entité avec ce note_config).
  // Le calque n'est PAS discriminant (Fissure & Faïençage partagent un calque) →
  // on stocke le NOM du note_config pour résoudre label/couleur via la légende.
  const defectName = viewer.activeNoteConfig?.name ?? null;

  for (const h of handles) {
    const id = findEntityByHandle(module, db, h);
    if (!id) continue;
    try {
      if (readEntityMeta(module, id) === null) {
        writeEntityMeta(module, id, {
          entityUid: uuid(),
          kind: getEntityType(module, id),
          markerNumber: null,
          createdAt: now,
          defectName,
          background: false,
          observationId: null,
        });
      }
    } catch {
      // La capture est best-effort : un souci de meta ne doit jamais casser le dessin.
    }
    deleteObj(id);
  }
}

/**
 * Conversion texte → MLeader : transfère le DR_META du texte source vers le MLeader
 * (continuité entityUid / createdAt / defectName, kind MLeader). Ainsi le repère hérite
 * de l'identité du texte. Si la source n'a pas de meta (dessin antérieur), capture une
 * identité neuve sur le MLeader.
 *
 * Non destructif pour la source : le texte masqué garde son meta mais est ignoré
 * partout car il est sur `UNDO_LAYER` (filtré). Sur undo, c'est le texte qui
 * redevient visible et porte l'identité — les deux ont le même meta, un seul est
 * sur un vrai calque à la fois.
 */
export function transferMetaOnConvert(
  viewer: DRDrawingViewer,
  fromHandle: string,
  toHandle: string,
): void {
  const module = viewer.module;
  const db = viewer.database;
  if (!db) return;
  const fromId = findEntityByHandle(module, db, fromHandle);
  const toId = findEntityByHandle(module, db, toHandle);
  try {
    if (!toId) return;
    const src = fromId ? readEntityMeta(module, fromId) : null;
    if (src) {
      writeEntityMeta(module, toId, { ...src, kind: EntityType.MLeader });
    } else if (readEntityMeta(module, toId) === null) {
      writeEntityMeta(module, toId, {
        entityUid: uuid(),
        kind: EntityType.MLeader,
        markerNumber: null,
        createdAt: Date.now(),
        defectName: viewer.activeNoteConfig?.name ?? null,
        background: false,
        observationId: null,
      });
    }
  } catch {
    // best-effort
  } finally {
    if (fromId) deleteObj(fromId);
    if (toId) deleteObj(toId);
  }
}
